import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
});

export class NewPasswordRequiredError extends Error {
  constructor() {
    super('A new password is required before signing in');
    this.name = 'NewPasswordRequiredError';
  }
}

type AuthContextValue = {
  isAuthenticated: boolean;
  email: string | null;
  idToken: string | null;
  newPasswordRequired: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<CognitoUserSession | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [pendingUser, setPendingUser] = useState<CognitoUser | null>(null);

  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) return;

    currentUser.getSession(
      (err: Error | null, currentSession: CognitoUserSession | null) => {
        if (!err && currentSession && currentSession.isValid()) {
          setSession(currentSession);
          setEmail(currentUser.getUsername());
        }
      }
    );
  }, []);

  const signIn = (emailInput: string, password: string) =>
    new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: emailInput, Pool: userPool });
      const authDetails = new AuthenticationDetails({
        Username: emailInput,
        Password: password,
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (newSession) => {
          setSession(newSession);
          setEmail(emailInput);
          setPendingUser(null);
          resolve();
        },
        onFailure: (err) => reject(err),
        newPasswordRequired: () => {
          setPendingUser(cognitoUser);
          reject(new NewPasswordRequiredError());
        },
      });
    });

  const completeNewPassword = (newPassword: string) =>
    new Promise<void>((resolve, reject) => {
      if (!pendingUser) {
        reject(new Error('No sign-in in progress'));
        return;
      }

      pendingUser.completeNewPasswordChallenge(
        newPassword,
        {},
        {
          onSuccess: (newSession) => {
            setSession(newSession);
            setEmail(pendingUser.getUsername());
            setPendingUser(null);
            resolve();
          },
          onFailure: (err) => reject(err),
        }
      );
    });

  const signOut = () => {
    userPool.getCurrentUser()?.signOut();
    setSession(null);
    setEmail(null);
    setPendingUser(null);
  };

  const value: AuthContextValue = {
    isAuthenticated: !!session,
    email,
    idToken: session ? session.getIdToken().getJwtToken() : null,
    newPasswordRequired: !!pendingUser,
    signIn,
    completeNewPassword,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
